import {
  GetPresignedUrlForXml,
  PrepareXmlFileForB2G,
  PrepareXmlFileForB2GResponse,
  ProcessB2GIntegration,
  XmlFileProcessingStatusSubscription,
  XmlRequestType,
} from "@tc/ui-dfe/query"
import axios from "axios"
import { useCallback, useState } from "react"
import { ulid } from "ulid"
import { useClient } from "urql"
import { useToast } from "./useToast"

export interface UploadResult {
  fileName: string
  xmlInputId: string
}

interface UseXmlSubmitResult {
  uploadProgress: number
  isUploading: boolean
  isProcessing: boolean
  errors: string[]
  uploadFile: (file: File, fileStore?: string) => Promise<UploadResult | null>
  submitXml: (
    upload: UploadResult,
    requestType: XmlRequestType,
  ) => Promise<{ success: boolean; responseId?: string }>
  reset: () => void
}

const parseErrorMessage = (error: string): string => {
  try {
    const parsed = JSON.parse(error)
    return parsed.message || parsed.validationMessage || error
  } catch {
    return error
  }
}

const getErrors = (status: PrepareXmlFileForB2GResponse): string[] | null => {
  if (
    status.status === "FAILED" ||
    status.status === "B2G_ERROR" ||
    status.errorMessages?.length
  ) {
    return status.errorMessages?.length
      ? status.errorMessages.map(parseErrorMessage)
      : ["Processing failed"]
  }
  return null
}

export const useXmlSubmit = (): UseXmlSubmitResult => {
  const [uploadProgress, setUploadProgress] = useState(0)
  const [isUploading, setIsUploading] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [errors, setErrors] = useState<string[]>([])

  const client = useClient()
  const toast = useToast()

  const createSubscription = (xmlInputId: string) => {
    let onReady: () => void
    let onPrepared: (status: PrepareXmlFileForB2GResponse) => void
    let onCompleted: (status: PrepareXmlFileForB2GResponse) => void

    const readyPromise = new Promise<void>((resolve) => {
      onReady = resolve
    })

    const preparedPromise = new Promise<PrepareXmlFileForB2GResponse>(
      (resolve) => {
        onPrepared = resolve
      },
    )

    const completedPromise = new Promise<PrepareXmlFileForB2GResponse>(
      (resolve, reject) => {
        onCompleted = resolve

        const { unsubscribe } = client
          .subscription(XmlFileProcessingStatusSubscription.toString(), {
            xmlInputId,
          })
          .subscribe((result) => {
            if (result.error) {
              unsubscribe()
              reject(result.error)
              return
            }

            const data = result.data as {
              __subscriptionReady?: boolean
              onXmlFileProcessed?: PrepareXmlFileForB2GResponse
            }

            if (data?.__subscriptionReady) {
              onReady()
              return
            }

            const status = data?.onXmlFileProcessed
            if (!status) return

            if (status.status === "PREPARED") {
              onPrepared(status)
            }

            if (
              status.status === "COMPLETED" ||
              status.status === "FAILED" ||
              status.status === "B2G_ERROR"
            ) {
              onPrepared(status)
              unsubscribe()
              onCompleted(status)
            }
          })
      },
    )

    return { readyPromise, preparedPromise, completedPromise }
  }

  const uploadFile = useCallback(
    async (
      file: File,
      fileStore = "b2g_payloads",
    ): Promise<UploadResult | null> => {
      const xmlInputId = ulid()

      try {
        setErrors([])
        setIsUploading(true)
        setUploadProgress(0)

        const urlResult = await client
          .query(GetPresignedUrlForXml.toString(), {
            fileStore,
            key: `${xmlInputId}/${xmlInputId}.xml`,
          })
          .toPromise()

        if (urlResult.error || !urlResult.data?.getPresignedUrl) {
          throw new Error("Failed to get upload URL")
        }

        await axios.put(urlResult.data.getPresignedUrl, file, {
          headers: { "Content-Type": file.type || "application/xml" },
          onUploadProgress: (e) =>
            setUploadProgress(
              e.total ? Math.round((e.loaded * 100) / e.total) : 0,
            ),
        })

        return { fileName: file.name, xmlInputId }
      } catch {
        toast.error("Upload Failed", "File upload error")
        return null
      } finally {
        setIsUploading(false)
      }
    },
    [client, toast],
  )

  const submitXml = useCallback(
    async (
      upload: UploadResult,
      requestType: XmlRequestType,
    ): Promise<{ success: boolean; responseId?: string }> => {
      const { fileName, xmlInputId } = upload

      try {
        setErrors([])
        setIsProcessing(true)

        const { readyPromise, preparedPromise, completedPromise } =
          createSubscription(xmlInputId)
        await readyPromise

        const prepareResult = await client
          .mutation(PrepareXmlFileForB2G.toString(), {
            input: { requestType, fileName: `${xmlInputId}.xml`, xmlInputId },
          })
          .toPromise()

        if (prepareResult.error || !prepareResult.data?.prepareXmlFileForB2G) {
          throw new Error(
            prepareResult.error?.message || "XML preparation failed",
          )
        }

        const preparedStatus = await preparedPromise
        const preparedErrors = getErrors(preparedStatus)
        if (preparedErrors) {
          setErrors(preparedErrors)
          return { success: false }
        }

        const b2gResult = await client
          .mutation(ProcessB2GIntegration.toString(), {
            input: {
              xmlInputId,
              b2gPayload: preparedStatus.b2gPayload || "",
              requestType,
              fileName: `${xmlInputId}.xml`,
            },
          })
          .toPromise()

        if (b2gResult.error) {
          throw new Error(b2gResult.error.message || "B2G integration failed")
        }

        const finalStatus = await completedPromise
        const finalErrors = getErrors(finalStatus)
        if (finalErrors) {
          setErrors(finalErrors)
          return { success: false }
        }

        return {
          success: true,
          responseId: finalStatus.responseId || undefined,
        }
      } catch {
        toast.error("Submission Failed", "XML submission error")
        return { success: false }
      } finally {
        setIsProcessing(false)
      }
    },
    [client, toast],
  )

  const reset = useCallback(() => {
    setUploadProgress(0)
    setIsUploading(false)
    setIsProcessing(false)
    setErrors([])
  }, [])

  return {
    uploadProgress,
    isUploading,
    isProcessing,
    errors,
    uploadFile,
    submitXml,
    reset,
  }
}
